import api from "@/lib/api";

export const MAX_SOURCE_BYTES = 50 * 1024 * 1024;
const MAX_EDGE = 4096;
const QUALITIES = [0.9, 0.86, 0.82, 0.76, 0.7];
const TARGET_BYTES = 8 * 1024 * 1024;

export const fmtBytes = (n) => (n >= 1024 * 1024 ? `${(n / 1048576).toFixed(n >= 10 * 1048576 ? 0 : 1)} Mo` : `${Math.max(1, Math.round(n / 1024))} Ko`).replace(".", ",");

async function decode(file) {
  if (typeof createImageBitmap === "function") {
    try { return await createImageBitmap(file, { imageOrientation: "from-image" }); } catch { /* fallback */ }
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((res, rej) => { const img = new Image(); img.onload = () => res(img); img.onerror = rej; img.src = url; });
  } finally { URL.revokeObjectURL(url); }
}

const toBlob = (canvas, q) => new Promise((res) => canvas.toBlob(res, "image/jpeg", q));

/** Redimensionne (≤ 4096 px), ré-encode en JPEG (90 → 70), supprime l'EXIF en conservant l'orientation. */
export async function compressImage(file) {
  if (!file.type.startsWith("image/")) return { file, before: file.size, after: file.size, compressed: false };
  if (file.size > MAX_SOURCE_BYTES) throw new Error(`Image trop volumineuse (${fmtBytes(file.size)}) — maximum 50 Mo avant compression.`);
  const img = await decode(file);
  const w = img.width, h = img.height, scale = Math.min(1, MAX_EDGE / Math.max(w, h));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(w * scale); canvas.height = Math.round(h * scale);
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  if (img.close) img.close();
  let blob = null;
  for (const q of QUALITIES) { blob = await toBlob(canvas, q); if (blob && blob.size <= TARGET_BYTES) break; }
  if (!blob) throw new Error("Compression impossible pour cette image.");
  const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
  return { file: new File([blob], name, { type: "image/jpeg", lastModified: Date.now() }), before: file.size, after: blob.size, compressed: true };
}

/** Compresse (photos uniquement) puis envoie chaque fichier avec suivi de progression. */
export async function uploadImages(files, urlFor, { compress = true, onProgress } = {}) {
  const list = [...files];
  let totalBefore = 0, totalAfter = 0;
  for (let i = 0; i < list.length; i++) {
    const src = list[i];
    onProgress?.({ index: i, total: list.length, phase: "compress", name: src.name, loaded: 0, size: src.size, before: totalBefore, after: totalAfter });
    const r = compress ? await compressImage(src) : { file: src, before: src.size, after: src.size, compressed: false };
    totalBefore += r.before; totalAfter += r.after;
    const fd = new FormData(); fd.append("file", r.file);
    await api.post(urlFor(r.file, i), fd, {
      onUploadProgress: (e) => onProgress?.({ index: i, total: list.length, phase: "upload", name: r.file.name, loaded: e.loaded, size: e.total || r.file.size, before: totalBefore, after: totalAfter }),
    });
  }
  onProgress?.({ index: list.length, total: list.length, phase: "done", loaded: 1, size: 1, before: totalBefore, after: totalAfter });
  return { before: totalBefore, after: totalAfter };
}
